import React, { useState } from "react";

interface QuantityControlProps {
  label?: string;
  value?: number;
  onChange?: (value: number) => void;
  min?: number;
  max?: number;
  placeholder?: string;
  helperText?: string;
  className?: string;
}

export const QuantityControl: React.FC<QuantityControlProps> = ({
  label = "Choose quantity:",
  value: controlledValue,
  onChange,
  min = 0,
  max = 99999,
  placeholder = "999",
  helperText = "Please select a 5 digit number from 0 to 9.",
  className = "",
}) => {
  const [internalValue, setInternalValue] = useState<number>(0);

  const isControlled = controlledValue !== undefined;
  const currentValue = isControlled ? controlledValue : internalValue;

  const handleValueChange = (newValue: number) => {
    const clampedValue = Math.max(min, Math.min(max, newValue));

    if (!isControlled) {
      setInternalValue(clampedValue);
    }

    onChange?.(clampedValue);
  };

  const handleDecrement = () => {
    handleValueChange(currentValue - 1);
  };

  const handleIncrement = () => {
    handleValueChange(currentValue + 1);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = parseInt(e.target.value) || 0;
    handleValueChange(inputValue);
  };

  return (
    <form className={`mx-auto ${className}`}>
      <label className="block mb-2 text-sm font-medium text-dark-700">
        {label}
      </label>
      <div className="relative flex items-center max-w-32">
        <button
          type="button"
          onClick={handleDecrement}
          disabled={currentValue <= min}
          className="bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-s-lg p-3 h-11 focus:ring-2 focus:ring-green-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:border-gray-300"
        >
          <svg
            className="w-3 h-3 text-gray-700"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 18 2"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M1 1h16"
            />
          </svg>
        </button>
        <input
          type="text"
          value={currentValue}
          onChange={handleInputChange}
          className="bg-white border-y border-gray-300 h-11 text-center text-gray-900 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent block w-full py-2.5 rounded-none"
          placeholder={placeholder}
          required
        />
        <button
          type="button"
          onClick={handleIncrement}
          disabled={currentValue >= max}
          className="bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-e-lg p-3 h-11 focus:ring-2 focus:ring-green-500 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:border-gray-300"
        >
          <svg
            className="w-3 h-3 text-gray-700"
            aria-hidden="true"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 18 18"
          >
            <path
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M9 1v16M1 9h16"
            />
          </svg>
        </button>
      </div>
      {helperText && <p className="mt-2 text-sm text-gray-500">{helperText}</p>}
    </form>
  );
};
